import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { LicenseService, License } from '../../firebase/services/licenseService';
import DomainManager from './DomainManager';
import LicenseVerifier from './LicenseVerifier';

export default function Licenses() {
  const { currentUser } = useAuth();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedLicense, setExpandedLicense] = useState<string | null>(null);

  const fetchLicenses = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      const userLicenses = await LicenseService.getUserLicenses(currentUser.uid);
      setLicenses(userLicenses);
    } catch (err) {
      console.error('Error fetching licenses:', err);
      setError('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [currentUser?.uid]);

  const copyToClipboard = async (licenseKey: string) => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopiedKey(licenseKey);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy license key:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading licenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-semibold text-gray-900">
                Lava Addons
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Welcome, {currentUser?.email}
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">My Licenses</h1>
            <Link
              to="/purchase"
              className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
            >
              Purchase New License
            </Link>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {licenses.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No licenses</h3>
              <p className="mt-1 text-sm text-gray-500">
                You haven't purchased any licenses yet.
              </p>
              <div className="mt-6">
                <Link
                  to="/purchase"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Purchase your first license
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {licenses.map((license) => (
                  <li key={license.id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">Lava Addons {license.licenseType}</p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(license.status)}`}>
                                {license.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">{license.licenseType} License</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-900">Expires: {formatDate(license.expiryDate)}</p>
                          <p className="text-sm text-gray-500">Purchased: {formatDate(license.purchaseDate)}</p>
                        </div>
                      </div>
                      
                      {/* License Key Section */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">License Key:</h4>
                        <div className="flex items-center space-x-2">
                          <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono text-gray-800">
                            {license.licenseKey}
                          </code>
                          <button
                            onClick={() => copyToClipboard(license.licenseKey)}
                            className="px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-900 border border-indigo-300 rounded-md hover:bg-indigo-50"
                          >
                            {copiedKey === license.licenseKey ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                      </div>

                      {/* License Verifier */}
                      <LicenseVerifier licenseKey={license.licenseKey} licenseType={license.licenseType} />
                      
                      {/* Domain Management Toggle */}
                      <div className="mt-4">
                        <button
                          onClick={() => setExpandedLicense(expandedLicense === license.id ? null : license.id || null)}
                          className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <span>Manage Domains</span>
                          <svg 
                            className={`ml-1 h-4 w-4 transform transition-transform ${expandedLicense === license.id ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>

                      {/* Domain Manager */}
                      {expandedLicense === license.id && (
                        <DomainManager 
                          license={license} 
                          onUpdate={fetchLicenses}
                        />
                      )}
                      
                      <div className="mt-4 flex space-x-3">
                        <button className="text-sm text-gray-600 hover:text-gray-900">
                          View Documentation
                        </button>
                        <button className="text-sm text-gray-600 hover:text-gray-900">
                          Renew License
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 